import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Save, Edit2, Trash2, X, Eye, FileText, FileSpreadsheet, AlertCircle, Factory, RefreshCw, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WeavingProductionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);
  const [woSearchQuery, setWoSearchQuery] = useState("");
  const [selectedWO, setSelectedWO] = useState(null);
  const [productionHistory, setProductionHistory] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    po_id: "",
    date: new Date().toISOString().split('T')[0],
    sku: "",
    size: "",
    design_name: "",
    gsm: "",
    pieces_produced: "",
    weight_produced: ""
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [deletingWO, setDeletingWO] = useState(null);
  const formRef = useRef(null);

  const canAddProduction = currentUser?.role === 'admin' || currentUser?.department?.toLowerCase() === 'weaving';
  const canDeleteWO = currentUser?.role === 'admin' || currentUser?.department?.toLowerCase() === 'weaving';

  useEffect(() => {
    fetchWorkOrders();
    fetchCurrentUser();
  }, []);

  // Filter work orders when search query changes
  useEffect(() => {
    if (!woSearchQuery.trim()) {
      setFilteredWorkOrders(workOrders);
    } else {
      const query = woSearchQuery.toLowerCase();
      const filtered = workOrders.filter(wo => 
        wo.wo_number?.toLowerCase().includes(query) ||
        wo.customer_name?.toLowerCase().includes(query) ||
        wo.customer_po_number?.toLowerCase().includes(query) ||
        wo.current_stage?.toLowerCase().includes(query) ||
        wo.status?.toLowerCase().includes(query) ||
        wo.size_items?.some(item => 
          item.size?.toLowerCase().includes(query) ||
          item.quality?.toLowerCase().includes(query)
        )
      );
      setFilteredWorkOrders(filtered);
    }
  }, [woSearchQuery, workOrders]);

  useEffect(() => {
    const woId = searchParams.get('wo');
    if (woId && workOrders.length > 0) {
      const wo = workOrders.find(w => w.id === woId);
      if (wo) handleWOSelect(woId);
    }
  }, [searchParams, workOrders]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (err) {
      console.error("Failed to fetch user:", err);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const weavingWOs = response.data.filter(wo => 
        wo.status !== 'cancelled'
      );
      setWorkOrders(weavingWOs);
      setFilteredWorkOrders(weavingWOs);
    } catch (err) {
      console.error("Failed to fetch work orders:", err);
    }
  };

  const handleWOSelect = async (woId) => {
    const wo = workOrders.find(w => w.id === woId);
    setSelectedWO(wo);
    setFormData({ ...formData, po_id: woId, sku: "", size: "", design_name: "", gsm: "" });
    setEditingEntry(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/weaving-production/${woId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductionHistory(response.data);
    } catch (err) {
      console.error("Failed to fetch production history:", err);
      setProductionHistory(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem('token');
      
      if (editingEntry) {
        await axios.put(`${API}/weaving-production/${editingEntry.id}`, {
          date: formData.date,
          size: formData.size,
          design_name: formData.design_name,
          gsm: formData.gsm,
          pieces_produced: parseInt(formData.pieces_produced),
          weight_produced: parseFloat(formData.weight_produced)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess("Production entry updated successfully!");
        setEditingEntry(null);
      } else {
        await axios.post(`${API}/weaving-production`, {
          ...formData,
          design_name: formData.design_name,
          gsm: formData.gsm
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess("Production entry added successfully!");
      }
      
      setFormData({ ...formData, sku: "", size: "", design_name: "", gsm: "", pieces_produced: "", weight_produced: "" });
      
      if (selectedWO) {
        const historyRes = await axios.get(`${API}/weaving-production/${selectedWO.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProductionHistory(historyRes.data);
      }
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Failed to save production:", err);
      setError(err.response?.data?.detail || "Failed to save production entry");
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      ...formData,
      date: entry.date,
      size: entry.size || "",
      design_name: entry.design_name || "",
      gsm: entry.gsm || "",
      sku: entry.sku || "",
      pieces_produced: entry.pieces_produced.toString(),
      weight_produced: entry.weight_produced.toString()
    });
    // Scroll to form after a short delay to ensure state is updated
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setFormData({
      ...formData,
      date: new Date().toISOString().split('T')[0],
      size: "", design_name: "", gsm: "", sku: "", pieces_produced: "", weight_produced: ""
    });
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this production entry?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/weaving-production/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Production entry deleted successfully!");
      
      if (selectedWO) {
        const historyRes = await axios.get(`${API}/weaving-production/${selectedWO.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProductionHistory(historyRes.data);
      }
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Failed to delete entry:", err);
      setError(err.response?.data?.detail || "Failed to delete production entry");
    }
  };

  const handleExportPDF = async (woId) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/work-order/${woId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedWO?.wo_number || 'WorkOrder'}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to export PDF:", err);
      setError("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async (woId) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/work-order/${woId}/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedWO?.wo_number || 'WorkOrder'}_Report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to export Excel:", err);
      setError("Failed to export Excel");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteWorkOrder = async () => {
    if (!selectedWO) return;
    
    if (!window.confirm(`⚠️ DELETE ${selectedWO.wo_number}?\n\nThis will permanently delete:\n- The work order\n- All production entries (weaving, dyeing, stitching)\n- All accessories\n\nThis action CANNOT be undone!`)) {
      return;
    }
    
    setDeletingWO(selectedWO.id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/work-orders/${selectedWO.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`${selectedWO.wo_number} deleted successfully!`);
      setSelectedWO(null);
      setProductionHistory(null);
      fetchWorkOrders(); // Refresh the list
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Failed to delete work order:", err);
      setError(err.response?.data?.detail || "Failed to delete work order");
    } finally {
      setDeletingWO(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Factory className="w-6 h-6" />
            Weaving Production
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Daily production tracking for weaving department</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchWorkOrders}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Link to="/create-work-order">
            <Button size="sm" data-testid="new-wo-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Work Order
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Work Order Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Active Work Orders</CardTitle>
            <CardDescription>Select a WO to track production</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={woSearchQuery}
                onChange={(e) => setWoSearchQuery(e.target.value)}
                placeholder="Search WO, customer, PO..."
                className="pl-9 h-9"
                data-testid="wo-search-input"
              />
              {woSearchQuery && (
                <X 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground cursor-pointer hover:text-foreground"
                  onClick={() => setWoSearchQuery("")}
                />
              )}
            </div>
            
            {/* Results count */}
            {woSearchQuery && (
              <div className="text-xs text-muted-foreground">
                Found {filteredWorkOrders.length} of {workOrders.length} work orders
              </div>
            )}
            
            {/* Work Orders List */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredWorkOrders.length === 0 ? (
                <div className="text-center py-8">
                  <Factory className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                  {woSearchQuery ? (
                    <p className="text-sm text-muted-foreground">No work orders match "{woSearchQuery}"</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-4">No work orders in weaving</p>
                      <Link to="/create-work-order">
                        <Button size="sm" variant="outline">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Work Order
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              ) : (
                filteredWorkOrders.map(wo => (
                  <div
                    key={wo.id}
                    onClick={() => handleWOSelect(wo.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedWO?.id === wo.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'hover:bg-muted border-border'
                    }`}
                    data-testid={`wo-select-${wo.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-sm">{wo.wo_number}</div>
                        <div className="text-xs opacity-80">{wo.customer_name}</div>
                        {wo.customer_po_number && (
                          <div className="text-xs opacity-60">PO: {wo.customer_po_number}</div>
                        )}
                      </div>
                      <Badge 
                        variant={wo.current_stage === 'shipped' ? 'default' : 'secondary'} 
                        className={`text-xs ${
                          wo.current_stage === 'shipped' ? 'bg-green-600 text-white' : 
                          wo.current_stage === 'ready' ? 'bg-amber-500 text-white' : 
                          wo.status === 'completed' ? 'bg-blue-600 text-white' : ''
                        }`}
                      >
                        {wo.current_stage === 'shipped' ? 'Shipped' : 
                         wo.current_stage === 'ready' ? 'Ready' : 
                         wo.current_stage}
                      </Badge>
                    </div>
                    <div className="text-xs opacity-70 mt-2">
                      {wo.total_qty_pcs} pcs | {(() => {
                        let kgs = parseFloat(wo.total_qty_weight || 0);
                        if (kgs === 0 && wo.order_items?.length > 0) {
                          kgs = wo.order_items.reduce((sum, item) => {
                            const k = parseFloat(item.order_kgs || 0);
                            if (k > 0) return sum + k;
                            const pcs = parseInt(item.order_pcs || 0);
                            const wt = parseFloat(item.wt_per_pc_gms || 0);
                            return sum + (wt > 0 && pcs > 0 ? (pcs * wt) / 1000 : 0);
                          }, 0);
                        }
                        return kgs.toFixed(2);
                      })()} KG
                    </div>
                    {wo.shipment_date && (
                      <div className="text-xs opacity-60 mt-1">
                        Ship: {new Date(wo.shipment_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Production Entry Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card ref={formRef}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">
                    {selectedWO ? `Production Entry - ${selectedWO.wo_number}` : 'Production Entry'}
                  </CardTitle>
                  {selectedWO && (
                    <CardDescription>
                      {selectedWO.customer_name} | PO: {selectedWO.customer_po_number}
                      {selectedWO.current_stage === 'shipped' && (
                        <span className="ml-2 text-red-600 font-semibold">( Shipped - No entries allowed )</span>
                      )}
                    </CardDescription>
                  )}
                </div>
                {selectedWO && canDeleteWO && (
                  <div className="flex gap-2">
                    <Link to={`/create-work-order?edit=${selectedWO.wo_number}`}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid="edit-wo-btn"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit WO
                      </Button>
                    </Link>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleDeleteWorkOrder}
                      disabled={deletingWO === selectedWO.id}
                      data-testid="delete-wo-btn"
                    >
                      {deletingWO === selectedWO.id ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Delete WO
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedWO ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Factory className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Select a Work Order to add production</p>
                </div>
              ) : !canAddProduction ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="text-yellow-600 w-5 h-5" />
                  <span className="text-yellow-800 font-medium">Only Weaving department can add production entries</span>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {selectedWO.size_items?.length > 0 && (
                    <div className="bg-muted/50 rounded-lg p-3 text-sm">
                      <div className="font-medium mb-2">Order Sizes:</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedWO.size_items.map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {item.size} - {item.quality} ({item.qty_pcs} pcs)
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Size & Design *</Label>
                      {selectedWO.size_items?.length > 0 ? (
                        <Select
                          value={formData.size && formData.design_name ? `${formData.size}|||${formData.design_name}|||${formData.gsm}` : ""}
                          onValueChange={(value) => {
                            const parts = value.split('|||');
                            const size = parts[0];
                            const design = parts[1];
                            const gsm = parts[2] || '';
                            const item = selectedWO.size_items.find(i => i.size === size && i.quality === design && String(i.gsm) === gsm);
                            setFormData({ 
                              ...formData, 
                              size: size,
                              design_name: design,
                              gsm: gsm,
                              sku: item ? `${item.size}-${item.quality}-${item.gsm}` : ""
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size & design" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedWO.size_items.map((item, idx) => (
                              <SelectItem key={idx} value={`${item.size}|||${item.quality}|||${item.gsm}`}>
                                {item.size} - {item.quality} ({item.gsm} GSM)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : selectedWO.order_items?.length > 0 ? (
                        <Select
                          value={formData.size && formData.design_name ? `${formData.size}|||${formData.design_name}|||${formData.gsm}` : ""}
                          onValueChange={(value) => {
                            const parts = value.split('|||');
                            const size = parts[0];
                            const design = parts[1];
                            const gsm = parts[2] || '';
                            const item = selectedWO.order_items.find(i => 
                              (i.size_cm === size || i.size === size) && 
                              (i.design_name === design || i.quality === design) &&
                              String(i.gsm || '') === gsm
                            );
                            setFormData({ 
                              ...formData, 
                              size: size,
                              design_name: design,
                              gsm: gsm,
                              sku: item ? `${size}-${design}-${gsm}` : ""
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size & design" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedWO.order_items.map((item, idx) => {
                              const size = item.size_cm || item.size || '';
                              const design = item.design_name || item.quality || '';
                              const gsm = String(item.gsm || '');
                              return (
                                <SelectItem key={idx} value={`${size}|||${design}|||${gsm}`}>
                                  {size} - {design} ({gsm} GSM)
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={formData.size}
                          onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                          placeholder="Enter size"
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pieces Produced *</Label>
                      <Input
                        type="number"
                        value={formData.pieces_produced}
                        onChange={(e) => setFormData({ ...formData, pieces_produced: e.target.value })}
                        placeholder="Enter pieces"
                        required
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Weight (KG) *</Label>
                      <Input
                        type="number"
                        value={formData.weight_produced}
                        onChange={(e) => setFormData({ ...formData, weight_produced: e.target.value })}
                        placeholder="Enter weight"
                        required
                        min="0.01"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                      ✅ {success}
                    </div>
                  )}

                  <div className="flex gap-3">
                    {editingEntry && (
                      <Button type="button" variant="outline" onClick={handleCancelEdit} className="flex-1">
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                    <Button type="submit" disabled={loading || selectedWO?.current_stage === 'shipped'} className={editingEntry ? 'flex-1' : 'w-full'} data-testid="submit-production">
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? "Saving..." : editingEntry ? "Update Entry" : "Save Entry"}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Production History */}
          {selectedWO && productionHistory && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Weaving Status - {selectedWO.wo_number}
                    </CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleExportPDF(selectedWO.id)} disabled={exporting}>
                      <FileText className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportExcel(selectedWO.id)} disabled={exporting}>
                      <FileSpreadsheet className="w-4 h-4 mr-1" />
                      Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Order Size-wise Summary */}
                <div className="bg-slate-50 border rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3 text-slate-700">Order Sizes (Total Qty to Weave)</h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-100">
                          <TableHead className="text-xs">Size</TableHead>
                          <TableHead className="text-xs">Quality</TableHead>
                          <TableHead className="text-xs">GSM</TableHead>
                          <TableHead className="text-xs text-right">Plan Pcs</TableHead>
                          <TableHead className="text-xs text-right">Plan Kgs</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(selectedWO.size_items?.length > 0
                          ? selectedWO.size_items.map((item, idx) => {
                              const pcs = parseInt(item.qty_pcs || 0);
                              const kgs = parseFloat(item.qty_weight || 0) || (parseFloat(item.wt_per_pc_gms || 0) > 0 && pcs > 0 ? (pcs * parseFloat(item.wt_per_pc_gms)) / 1000 : 0);
                              return (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium text-xs">{item.size}</TableCell>
                                  <TableCell className="text-xs">{item.quality}</TableCell>
                                  <TableCell className="text-xs">{item.gsm}</TableCell>
                                  <TableCell className="text-right font-semibold text-xs">{pcs.toLocaleString()}</TableCell>
                                  <TableCell className="text-right text-xs">{kgs.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })
                          : selectedWO.order_items?.map((item, idx) => {
                              const pcs = parseInt(item.plan_pcs || item.order_pcs || item.qty_pcs || 0);
                              const kgs = parseFloat(item.plan_kgs || item.order_kgs || item.qty_weight || 0) || (parseFloat(item.wt_per_pc_gms || 0) > 0 && pcs > 0 ? (pcs * parseFloat(item.wt_per_pc_gms)) / 1000 : 0);
                              return (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium text-xs">{item.size_cm || item.size || ''}</TableCell>
                                  <TableCell className="text-xs">{item.design_name || item.quality || ''}</TableCell>
                                  <TableCell className="text-xs">{item.gsm}</TableCell>
                                  <TableCell className="text-right font-semibold text-xs">{pcs.toLocaleString()}</TableCell>
                                  <TableCell className="text-right text-xs">{kgs.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })
                        )}
                        {(() => {
                          const items = selectedWO.order_items?.length > 0 ? selectedWO.order_items : (selectedWO.size_items || []);
                          let totalPcs = 0;
                          let totalKg = 0;
                          items.forEach(item => {
                            const pcs = parseInt(item.plan_pcs || item.order_pcs || item.qty_pcs || 0);
                            let kgs = parseFloat(item.plan_kgs || item.order_kgs || item.qty_weight || 0);
                            if (kgs === 0) {
                              const wt = parseFloat(item.wt_per_pc_gms || 0);
                              if (wt > 0 && pcs > 0) kgs = (pcs * wt) / 1000;
                            }
                            totalPcs += pcs;
                            totalKg += kgs;
                          });
                          return (
                            <TableRow className="bg-slate-100 font-bold">
                              <TableCell colSpan={3} className="text-xs">Total</TableCell>
                              <TableCell className="text-right text-xs">{totalPcs.toLocaleString()} pcs</TableCell>
                              <TableCell className="text-right text-xs">{totalKg.toFixed(2)} KG</TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Weaving Progress Stats */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3 text-green-700">🧵 Weaving Progress</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-blue-600">{productionHistory.total_pieces}</div>
                      <div className="text-xs text-muted-foreground">Plan Qty (Pcs)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-blue-600">{parseFloat(productionHistory.total_kg || 0).toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">Plan Qty (KG)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-green-600">{productionHistory.produced_pieces}</div>
                      <div className="text-xs text-muted-foreground">Weaved (Pcs)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-green-600">{productionHistory.produced_kg}</div>
                      <div className="text-xs text-muted-foreground">Weaved (KG)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-orange-600">{productionHistory.balance_pieces}</div>
                      <div className="text-xs text-muted-foreground">Balance (Pcs)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-orange-600">{parseFloat(productionHistory.balance_kg || 0).toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">Balance (KG)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border col-span-2">
                      <div className="text-2xl font-bold text-purple-600">{productionHistory.completion_percentage}%</div>
                      <div className="text-xs text-muted-foreground">Completion</div>
                    </div>
                  </div>
                  
                  {/* Size-wise Production Summary */}
                  {productionHistory.size_wise_data?.length > 0 && (
                    <div className="mt-4">
                      <h5 className="font-medium text-sm mb-2 text-green-700">Size-wise Production</h5>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-green-100">
                              <TableHead className="text-xs">Size</TableHead>
                              <TableHead className="text-xs">Quality</TableHead>
                              <TableHead className="text-xs text-right">Plan Pcs</TableHead>
                              <TableHead className="text-xs text-right">Produced</TableHead>
                              <TableHead className="text-xs text-right">Balance</TableHead>
                              <TableHead className="text-xs text-right">Balance Wt</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {productionHistory.size_wise_data.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{item.size}</TableCell>
                                <TableCell>{item.quality}</TableCell>
                                <TableCell className="text-right">{item.plan_pcs}</TableCell>
                                <TableCell className="text-right text-green-600 font-semibold">{item.produced_pcs}</TableCell>
                                <TableCell className="text-right text-orange-600">{item.balance_pcs}</TableCell>
                                <TableCell className="text-right font-mono text-blue-700">
                                  {item.balance_kg ? `${item.balance_kg} Kg` : (item.balance_pcs > 0 && item.wt_per_pc_gms > 0 ? `${((item.balance_pcs * item.wt_per_pc_gms) / 1000).toFixed(2)} Kg` : '-')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  
                  <Progress value={Math.min(productionHistory.completion_percentage, 100)} className="h-2 mt-3" />
                  
                  {/* Weight Variance Section - Per Size+Design Analysis */}
                  {(() => {
                    // Build a map of size+design -> expected weight from order_items or size_items
                    const sizeDesignWeightMap = {};
                    const orderItems = selectedWO.order_items || [];
                    const sizeItems = selectedWO.size_items || [];
                    
                    // First try order_items (newer format) - use SIZE + DESIGN as key
                    orderItems.forEach(item => {
                      const size = (item.size_cm || item.size || '').toLowerCase().trim();
                      const design = (item.design_name || item.quality || '').toLowerCase().trim();
                      const wt = parseFloat(item.wt_per_pc_gms) || 0;
                      if (size && wt > 0) {
                        // Create key with both size and design
                        const key = design ? `${size}|||${design}` : size;
                        sizeDesignWeightMap[key] = wt;
                        // Also store by size-only as fallback for entries without design
                        if (!sizeDesignWeightMap[size]) {
                          sizeDesignWeightMap[size] = wt;
                        }
                      }
                    });
                    
                    // Fallback to size_items (older format)
                    if (Object.keys(sizeDesignWeightMap).length === 0) {
                      sizeItems.forEach(item => {
                        const size = (item.size || '').toLowerCase().trim();
                        const design = (item.quality || '').toLowerCase().trim();
                        const wt = parseFloat(item.wt_per_pc_gms) || 0;
                        if (size && wt > 0) {
                          const key = design ? `${size}|||${design}` : size;
                          sizeDesignWeightMap[key] = wt;
                          if (!sizeDesignWeightMap[size]) {
                            sizeDesignWeightMap[size] = wt;
                          }
                        }
                      });
                    }
                    
                    const hasExpectedWeights = Object.keys(sizeDesignWeightMap).length > 0;
                    
                    // Calculate per-size+design statistics from production entries
                    const sizeDesignStats = {};
                    (productionHistory.entries || []).forEach(entry => {
                      const size = (entry.size || '').toLowerCase().trim();
                      const design = (entry.design_name || '').toLowerCase().trim();
                      // Try to find expected weight using size+design first, then size only
                      const key = design ? `${size}|||${design}` : size;
                      const displayKey = design ? `${entry.size} - ${entry.design_name}` : entry.size;
                      const expectedWt = sizeDesignWeightMap[key] || sizeDesignWeightMap[size] || 0;
                      
                      if (!sizeDesignStats[displayKey]) {
                        sizeDesignStats[displayKey] = { pieces: 0, weight: 0, expectedWt: expectedWt };
                      }
                      sizeDesignStats[displayKey].pieces += entry.pieces_produced || 0;
                      sizeDesignStats[displayKey].weight += entry.weight_produced || 0;
                    });
                    
                    // Calculate overall weighted stats
                    let totalPieces = 0;
                    let totalWeight = 0;
                    let withinToleranceCount = 0;
                    let totalSizes = 0;
                    
                    Object.entries(sizeDesignStats).forEach(([key, stats]) => {
                      totalPieces += stats.pieces;
                      totalWeight += stats.weight;
                      if (stats.expectedWt > 0 && stats.pieces > 0) {
                        const actualWt = (stats.weight / stats.pieces) * 1000;
                        const variance = ((actualWt - stats.expectedWt) / stats.expectedWt) * 100;
                        if (variance >= -2 && variance <= 2) withinToleranceCount++;
                        totalSizes++;
                      }
                    });
                    
                    const overallActualWtGms = totalPieces > 0 ? (totalWeight / totalPieces) * 1000 : 0;
                    const allWithinTolerance = totalSizes > 0 && withinToleranceCount === totalSizes;
                    
                    return productionHistory.produced_pieces > 0 ? (
                      <div className={`mb-3 p-3 rounded-lg border-2 ${!hasExpectedWeights ? 'bg-blue-50 border-blue-300' : allWithinTolerance ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-400'}`}>
                        <h5 className={`font-semibold text-sm mb-2 ${!hasExpectedWeights ? 'text-blue-700' : allWithinTolerance ? 'text-green-700' : 'text-red-700'}`}>
                          ⚖️ Weight Analysis by Size & Design {!hasExpectedWeights && '(Expected Wt/Pc not set in Work Order)'}
                        </h5>
                        {hasExpectedWeights ? (
                          <div className="space-y-2">
                            {Object.entries(sizeDesignStats).map(([sizeDesign, stats]) => {
                              if (stats.pieces === 0) return null;
                              const actualWtGms = (stats.weight / stats.pieces) * 1000;
                              const expectedWt = stats.expectedWt;
                              const variance = expectedWt > 0 ? ((actualWtGms - expectedWt) / expectedWt) * 100 : 0;
                              const isOk = variance >= -2 && variance <= 2;
                              return (
                                <div key={sizeDesign} className={`grid grid-cols-5 gap-2 p-2 rounded ${isOk ? 'bg-green-50' : 'bg-red-50'}`}>
                                  <div className="font-medium text-sm">{sizeDesign}</div>
                                  <div className="text-center">
                                    <div className="text-sm font-bold">{expectedWt > 0 ? `${expectedWt}g` : '-'}</div>
                                    <div className="text-xs text-muted-foreground">Expected</div>
                                  </div>
                                  <div className="text-center">
                                    <div className={`text-sm font-bold ${isOk ? 'text-green-600' : 'text-red-600'}`}>{actualWtGms.toFixed(1)}g</div>
                                    <div className="text-xs text-muted-foreground">Actual</div>
                                  </div>
                                  <div className="text-center">
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${isOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      {expectedWt > 0 ? `${variance > 0 ? '+' : ''}${variance.toFixed(1)}%` : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="text-center">
                                    <span className={`text-xs ${isOk ? 'text-green-600' : 'text-red-600'}`}>
                                      {expectedWt > 0 ? (isOk ? '✓ OK' : '✗ Off') : '-'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                            <div className="text-xs text-muted-foreground mt-2 text-center">
                              {withinToleranceCount}/{totalSizes} size-designs within ±2% tolerance
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white p-2 rounded text-center">
                              <div className="text-lg font-bold text-blue-600">{overallActualWtGms.toFixed(1)} g</div>
                              <div className="text-xs text-muted-foreground">Actual Avg Wt/Pc</div>
                            </div>
                            <div className="bg-white p-2 rounded text-center">
                              <div className="text-sm font-medium text-blue-600">
                                Set "Wt/Pc (gms)" in Work Order for variance tracking
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-white p-3 rounded-lg text-center border border-orange-200">
                      <div className="text-2xl font-bold text-orange-600">{productionHistory.balance_pieces}</div>
                      <div className="text-xs text-muted-foreground">Balance (Pcs)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border border-orange-200">
                      <div className="text-2xl font-bold text-orange-600">{productionHistory.balance_kg || 0}</div>
                      <div className="text-xs text-muted-foreground">Balance (KG)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border border-primary">
                      <div className="text-2xl font-bold text-primary">{productionHistory.completion_percentage}%</div>
                      <div className="text-xs text-muted-foreground">Complete</div>
                    </div>
                  </div>
                </div>

                <Progress value={Math.min(productionHistory.completion_percentage, 100)} className="h-2" />

                {/* Entries Table with Per-Entry Weight Variance */}
                {productionHistory.entries?.length > 0 ? (
                  (() => {
                    // Build a map of size+design -> expected weight from order_items or size_items
                    const sizeDesignWeightMap = {};
                    const orderItems = selectedWO.order_items || [];
                    const sizeItems = selectedWO.size_items || [];
                    
                    // First try order_items (newer format) - use SIZE + DESIGN as key
                    orderItems.forEach(item => {
                      const size = (item.size_cm || item.size || '').toLowerCase().trim();
                      const design = (item.design_name || item.quality || '').toLowerCase().trim();
                      const wt = parseFloat(item.wt_per_pc_gms) || 0;
                      if (size && wt > 0) {
                        const key = design ? `${size}|||${design}` : size;
                        sizeDesignWeightMap[key] = wt;
                        // Also store by size-only as fallback
                        if (!sizeDesignWeightMap[size]) {
                          sizeDesignWeightMap[size] = wt;
                        }
                      }
                    });
                    
                    // Fallback to size_items (older format)
                    if (Object.keys(sizeDesignWeightMap).length === 0) {
                      sizeItems.forEach(item => {
                        const size = (item.size || '').toLowerCase().trim();
                        const design = (item.quality || '').toLowerCase().trim();
                        const wt = parseFloat(item.wt_per_pc_gms) || 0;
                        if (size && wt > 0) {
                          const key = design ? `${size}|||${design}` : size;
                          sizeDesignWeightMap[key] = wt;
                          if (!sizeDesignWeightMap[size]) {
                            sizeDesignWeightMap[size] = wt;
                          }
                        }
                      });
                    }
                    
                    const hasExpectedWeight = Object.keys(sizeDesignWeightMap).length > 0;
                    
                    return (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Size</TableHead>
                            <TableHead>Design</TableHead>
                            <TableHead className="text-right">Pieces</TableHead>
                            <TableHead className="text-right">Weight</TableHead>
                            <TableHead className="text-right">Avg Wt/Pc</TableHead>
                            {hasExpectedWeight && <TableHead className="text-center">Expected</TableHead>}
                            {hasExpectedWeight && <TableHead className="text-center">Variance</TableHead>}
                            <TableHead>By</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {productionHistory.entries.map((entry, idx) => {
                            const entrySize = (entry.size || '').toLowerCase().trim();
                            const entryDesign = (entry.design_name || '').toLowerCase().trim();
                            // Try size+design first, then size only
                            const key = entryDesign ? `${entrySize}|||${entryDesign}` : entrySize;
                            const expectedWtForEntry = sizeDesignWeightMap[key] || sizeDesignWeightMap[entrySize] || 0;
                            const entryWtPerPcGms = entry.pieces_produced > 0 
                              ? (entry.weight_produced / entry.pieces_produced) * 1000 
                              : 0;
                            const entryVariance = expectedWtForEntry > 0 && entryWtPerPcGms > 0
                              ? ((entryWtPerPcGms - expectedWtForEntry) / expectedWtForEntry) * 100
                              : 0;
                            const isWithinTolerance = entryVariance >= -2 && entryVariance <= 2;
                            
                            return (
                              <TableRow key={entry.id || idx}>
                                <TableCell className="text-sm">{new Date(entry.date).toLocaleDateString()}</TableCell>
                                <TableCell className="text-sm">{entry.size}</TableCell>
                                <TableCell className="text-sm text-muted-foreground">{entry.design_name || '-'}</TableCell>
                                <TableCell className="text-right font-semibold">{entry.pieces_produced}</TableCell>
                                <TableCell className="text-right font-semibold">{entry.weight_produced} KG</TableCell>
                                <TableCell className="text-right">
                                  <span className={`font-semibold ${hasExpectedWeight && expectedWtForEntry > 0 ? (isWithinTolerance ? 'text-green-600' : 'text-red-600') : 'text-slate-600'}`}>
                                    {entryWtPerPcGms.toFixed(1)} g
                                  </span>
                                </TableCell>
                                {hasExpectedWeight && (
                                  <TableCell className="text-center text-sm text-muted-foreground">
                                    {expectedWtForEntry > 0 ? `${expectedWtForEntry}g` : '-'}
                                  </TableCell>
                                )}
                                {hasExpectedWeight && (
                                  <TableCell className="text-center">
                                    {expectedWtForEntry > 0 ? (
                                      <span className={`px-2 py-1 rounded text-xs font-semibold ${isWithinTolerance ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {entryVariance > 0 ? '+' : ''}{entryVariance.toFixed(1)}%
                                      </span>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                )}
                                <TableCell className="text-xs text-muted-foreground">{entry.created_by}</TableCell>
                                <TableCell className="text-center">
                                  {canAddProduction && (
                                    <div className="flex justify-center gap-1">
                                      <Button variant="outline" size="sm" className="min-h-[44px] px-3" onClick={() => handleEditEntry(entry)} data-testid={`edit-entry-${entry.id}`}>
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      <Button variant="outline" size="sm" className="min-h-[44px] px-3 text-destructive" onClick={() => handleDeleteEntry(entry.id)} data-testid={`delete-entry-${entry.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    );
                  })()
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    No production entries yet
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
